import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Pause, Play, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader } from "@/components/common";
import { src } from "@/components/memories/MemoryParts";

const ORANGE = "var(--glow-home)";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const STATS = [["photos", "Photos"], ["milestones", "Milestones"], ["events", "Events"], ["chores_done", "Chores done"], ["books_finished", "Books finished"], ["stars", "Stars earned"]];

function Slideshow({ photos, onClose }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % photos.length), 5000);
    return () => clearTimeout(t);
  }, [i, paused, photos.length]);
  useEffect(() => { const on = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", on); return () => window.removeEventListener("keydown", on); }, [onClose]);
  const p = photos[i];
  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="slideshow" data-reduced-motion={reduced ? "true" : "false"}>
      <img key={p.id} src={src(p.full)} alt={p.caption || "Memory"} className={`absolute inset-0 m-auto max-h-full max-w-full object-contain ${reduced ? "animate-[q-fade_600ms_ease]" : "animate-[q-kenburns_5s_ease-out_forwards]"}`} data-testid="slideshow-image" />
      {p.caption && <p className="absolute bottom-16 left-0 right-0 text-center font-display text-xl font-bold text-white drop-shadow">{p.caption}</p>}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
        <button className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white" aria-label={paused ? "Play" : "Pause"} onClick={() => setPaused(!paused)} data-testid="slideshow-pause">{paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}</button>
        <span className="self-center text-sm text-white/70" data-testid="slideshow-position">{i + 1} / {photos.length}</span>
        <button className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white" aria-label="Close" onClick={onClose} data-testid="slideshow-close"><X className="h-5 w-5" /></button>
      </div>
    </div>
  );
}

export default function YearReview() {
  const { hub } = useHub();
  const { year } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => { api.get(`/organizations/${hub.id}/memories/review/${year}`).then(({ data }) => setD(data)).catch((e) => { setErr(errMsg(e)); if (e?.response?.status !== 404) toast.error(errMsg(e)); }); }, [hub.id, year]);
  const close = useCallback(() => setShow(false), []);
  if (err) return <EmptyState title={err} rgb={ORANGE} testid="review-empty" />;
  if (!d) return <Loader />;
  return (
    <div data-testid="year-review-page">
      <Link to={`/h/${hub.id}/memories`} className="mb-3 inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-q-muted hover:text-q-text" data-testid="review-back"><ChevronLeft className="h-4 w-4" /> Memories</Link>
      <h1 className="font-display text-4xl font-extrabold text-q-text sm:text-5xl lg:text-6xl" data-testid="review-title">{d.year} in review</h1>
      <div className="my-6 grid grid-cols-3 gap-1.5 overflow-hidden rounded-3xl" data-testid="review-collage">
        {d.cover.map((p, i) => <img key={p.id} src={src(p.thumb)} alt="" className={`aspect-square h-full w-full object-cover ${i === 0 ? "col-span-2 row-span-2" : ""}`} />)}
      </div>
      {d.slideshow.length > 0 && <button className="q-btn q-btn-primary mb-8 min-h-[44px]" onClick={() => setShow(true)} data-testid="slideshow-btn"><Play className="h-4 w-4" /> Play slideshow</button>}
      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="review-stats">
        {STATS.map(([k, l]) => <div key={k} className="q-card p-4"><p className="font-display text-3xl font-extrabold" style={{ color: `rgb(${ORANGE})` }} data-testid={`stat-${k}`}>{d.stats[k]}</p><p className="text-sm text-q-muted">{l}</p></div>)}
      </div>
      {d.months.map((m) => (
        <section key={m.month} className="mb-6" data-testid={`review-month-${m.month}`}>
          <h2 className="mb-2 text-xs font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${ORANGE})` }}>{MONTHS[m.month - 1]}</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {m.items.map((it) => (it.type === "milestone"
              ? <div key={it.id} className="q-card flex aspect-square flex-col justify-end p-3" data-testid={`review-milestone-${it.id}`}><span className="text-3xl">{it.emoji}</span><p className="font-display font-extrabold text-q-text">{it.title}</p></div>
              : <img key={it.id} src={src(it.thumb)} alt={it.caption || ""} className="aspect-square w-full rounded-xl object-cover" />))}
          </div>
        </section>
      ))}
      {show && <Slideshow photos={d.slideshow} onClose={close} />}
    </div>
  );
}
